<template lang="pug">
div(data-testId='cartGrid')
  //- template(v-if='cartData')
  template(v-for='(cartItem, index) in cartStore.cart.items' :key='cartItem.lineId')
    div(class='grid grid-cols-cart-layout bg-white')
      template(v-if='index === 0')
        div(class='p-3 uppercase text-black font-bold border-b hidden col-span-3 lg:col-span-2 md:block') Products In Cart ({{ cartStore.cart.itemCount }})
        div(class='p-3 uppercase text-black font-bold border-b hidden text-right lg:block') Price
        div(class='p-3 uppercase text-black font-bold border-b hidden text-center md:block') QTY
        div(class='p-3 uppercase text-black font-bold border-b hidden text-center md:block') Subtotal

      template(v-if='index > 0')
        div(class='border-t col-span-full my-3 lg:m-3')

      //- col 1 - Image
      div(class='col-span-full md:p-3 md:pl-0 md:col-span-1 md:row-span-2 lg:pl-3')
        img(
          v-if='cartItem.image'
          :src='urls.getImageUrl(cartItem.image.key, cartItem.image.filename, 474, 354)'
          width='474'
          height='354'
          alt='product image'
          class='border w-full max-w-[474px] md:max-w-[142px] lg:max-w-[120px] xl:max-w-[142px]'
        )

      //- col 2 - Details
      div(class='pt-2 pb-6 text-sm col-span-4 md:p-3 md:col-span-2 lg:col-span-1')
        div(class='space-y-1')
          div(class='text-xl font-bold md:text-base')
            UniversalLink(data-testid='productName' :link='`/p/${cartItem.productLineSlug}/${cartItem.skuSlug}/`')
              InlineHtml(:text='cartItem.productName')

          div
            div(class='text-black font-bold') Part #
            //- API doesn't return mpn so do we need this?
            div {{ cartItem.mpn || cartItem.sku }}

          div(v-if='cartItem.fitmentData && cartItem.fitmentData.year' class='relative')
            FitmentGuaranteedFit(
              v-if='$sitewideConfig.config.guaranteedFitEnabled && cartItem.guaranteedFit'
              :type='cartItem.guaranteedFit'
            )
            div(v-else class='text-black font-bold') Vehicle
            div
              | {{ cartItem.fitmentData.year }}
              | {{ cartItem.fitmentData.makeName }}
              | {{ cartItem.fitmentData.modelName }}
              InlineHtml(
                v-if='cartItem.fitmentData.bedName'
                class='before:content-[",_"]'
                :text='cartItem.fitmentData.bedName'
              )
              InlineHtml(
                v-if='cartItem.fitmentData.bodyName'
                class='before:content-[",_"]'
                :text='cartItem.fitmentData.bodyName'
              )
              InlineHtml(
                v-if='cartItem.fitmentData.engineName'
                class='before:content-[",_"]'
                :text='cartItem.fitmentData.engineName'
              )
              InlineHtml(
                v-if='cartItem.fitmentData.appNote'
                class='before:content-[",_"]'
                :text='cartItem.fitmentData.appNote'
              )

          div(
            v-if='cartItem.shipping && (cartItem.shipping.estimatedDeliveryDate || ($sitewideConfig.config.sameDayShippingEnabled && cartItem.shipping.sameDay))'
          )
            div(class='text-black font-bold') Shipping
            div
              Shipping(:data='cartItem')

            SameDayShippingLogo(
              v-if='$sitewideConfig.config.sameDayShippingEnabled && cartItem.shipping.sameDay'
              class='w-full max-w-[100px]'
            )

          template(v-if='cartItem.info')
            template(v-for='attribute in cartItem.info' :key='`${attribute.key}-${attribute.value}`')
              div(class='uppercase text-black font-bold') {{ attribute.key }}
              div
                InlineHtml(:text='attribute.value')

          div(v-if='cartItem.meta?.notes')
            div(class='uppercase text-black font-bold') Notes
            div
              InlineHtml(:text='cartItem.meta.notes')

          //- Promo
          div(v-if='cartItem.discount?.text' class='text-success')
            InlineHtml(:text='cartItem.discount.text')

          div(v-if='cartItem.promo' class='text-success')
            InlineHtml(:text='cartItem.promo.text')
            br
            a(v-if='cartItem.promo.url' :href='cartItem.promo.url' target='__blank' class='text-inherit') More information

      //- col 3 - wide
      div(class='pb-6 col-span-full md:p-3 md:col-span-4 md:col-start-2 md:order-last lg:pb-4')
        div(class='max-w-xs space-y-6')
          ExtendCart(v-if='isIntegrationEnabled("extend")' :item='cartItem')

          Installernet(
            v-if='isIntegrationEnabled("geolocation") && isIntegrationEnabled("installernet") && cartItem?.shipping && cartItem?.shipping?.sameDay'
            mode='CHECKOUT'
            :item='cartItem'
          )

      //- col 4 - Price (only on Large)
      div(class='font-bold text-right hidden md:p-3 lg:col-span-1 lg:block')
        div(class='text-black' :class='{ "line-through": cartItem.originalPrice !== cartItem.salePrice }') {{ formatCents(cartItem.originalPrice) }}
        div(v-if='cartItem.discount' class='text-sm text-danger-dark') {{ formatCents(cartItem.discount.amount * -1) }}

        ExtendPrice(
          v-if='isIntegrationEnabled("extend") && cartItem?.warranty'
          :warranty='cartItem.warranty'
          :show-item-price='true'
        )

        InstallernetPrice(
          v-if='isIntegrationEnabled("installernet") && cartItem?.installation'
          :installation='cartItem.installation'
          :show-item-price='true'
        )

      //- col 5 - QTY
      div(class='text-center col-span-2 md:p-3 md:col-span-1')
        CheckoutItemActions(:item='cartItem')

      //- col 6 - Subtotal
      div(class='col-span-2 p-3 md:block md:col-span-1')
        div(data-testid='cartSubtotal' class='font-bold text-right text-danger-dark lg:text-black') {{ formatCents(cartItem.saleSubtotal) }}

  template(v-if='$device.value.isDesktop')
    div(v-if='isIntegrationEnabled("cognito")')
      Wishlist
        template(#default)
          div(class='my-4 capitalize text-3xl font-bold')
            | Saved Items
    CheckoutNeedHelp
</template>

<script setup lang="ts">
const { $speedcurve, $uiEvents } = useNuxtApp()
const { isIntegrationEnabled } = useUtils()
const urls = useUrls()
const rootStore = useRootStore()
const cartStore = useCartStore()
const { translateTemplate } = useTemplateTranslator()

useMetaTags({
  title: translateTemplate('Cart [TITLE_SUFFIX]'),
  isNoIndex: true,
})

onMounted(() => {
  $uiEvents.$emit('cartViewed')
})

onBeforeMount(() => {
  $speedcurve.track('Cart Page')
})

rootStore.isSimpleMode = false
</script>
